import './MatchesView.css'

import { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import { session, matches } from '../store'

import { useRequireAuthentication } from '../hooks/useRequireAuthentication'
import { useRequireOnboarding } from '../hooks/useRequireOnboarding'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { MatchCarousel } from '../components/MatchCarousel'
import { Button } from '../components/Button'

const freeMatchLimit = 8
const paidMatchLimit = 16

export function MatchesView() {
  useRequireAuthentication()
  useRequireOnboarding()

  const navigate = useNavigate()
  const location = useLocation()
  const [, setBreadcrumbs] = useBreadcrumbs()
  const hasRestoredSelection = useRef(false)

  const authStatus = useAtomValue(session.sessionStatusAtom)
  const currentSession = useAtomValue(session.sessionAtom)
  const matchList = useAtomValue(matches.matchesAtom)
  const isLoading = useAtomValue(matches.matchesIsLoadingAtom)
  const error = useAtomValue(matches.matchesErrorAtom)
  const selectedMatchId = useAtomValue(matches.selectedMatchIdAtom)

  const fetchMatches = useSetAtom(matches.fetchMatchesAtom)
  const selectMatch = useSetAtom(matches.selectMatchAtom)

  // Set breadcrumbs
  useEffect(() => {
    setBreadcrumbs([{ label: 'Your Matches' }])
  }, [setBreadcrumbs])

  // Restore selected match if returning from profile
  useEffect(() => {
    const state = location.state as { selectedMatchId?: string } | null
    if (state?.selectedMatchId && !hasRestoredSelection.current) {
      selectMatch(state.selectedMatchId)
      hasRestoredSelection.current = true
    }
  }, [location.state, selectMatch])

  useEffect(() => {
    if (authStatus === 'authenticated') {
      fetchMatches()
    }
  }, [authStatus, fetchMatches])

  if (authStatus !== 'authenticated') {
    return (
      <div className="matches-view">
        <div className="matches-view__loading">Loading...</div>
      </div>
    )
  }

  const handleSelectMatch = (matchId: string) => {
    const match = matchList.find((m) => m.id === matchId)
    if (match) {
      // Reset the flag so it can restore selection when we come back
      hasRestoredSelection.current = false
      navigate(`/matches/${match.profile.id}`, {
        state: { selectedMatchId: matchId },
      })
    }
  }

  const calculateEmptySlots = () => {
    if (!currentSession) return 0
    const isPaid = currentSession.user.subscriptionTier === 'paid'
    const limit = isPaid ? paidMatchLimit : freeMatchLimit
    return Math.max(0, limit - matchList.length)
  }

  const hasApprovedPhotos =
    currentSession?.user.profile?.approvedPhotoIds &&
    currentSession.user.profile.approvedPhotoIds.length > 0

  return (
    <div className="matches-view">
      {isLoading && <div className="matches-view__loading">Loading your matches...</div>}

      {error && <div className="matches-view__error">{error}</div>}

      {!isLoading && !error && !hasApprovedPhotos && (
        <div className="matches-view__empty">
          <p className="matches-view__empty-text">
            You need at least one approved photo with you in it before you can be matched.
          </p>
          <Button kind="ghost" text="Go to Photos" onClick={() => navigate('/profile/photos')} />
        </div>
      )}

      {!isLoading && !error && hasApprovedPhotos && (
        <MatchCarousel
          matches={matchList}
          emptySlots={calculateEmptySlots()}
          {...(currentSession?.user.resetRemainingMatchCountAt !== undefined && {
            resetAt: currentSession.user.resetRemainingMatchCountAt,
          })}
          selectedMatchId={selectedMatchId}
          onSelectMatch={handleSelectMatch}
        />
      )}
    </div>
  )
}
